import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import {
  SpaceDetailsAccessGrantSource,
  SpaceDetailsAccessGrantedVia,
} from "../components/AccessCells/types";
import { USER_GROUPS_WITH_ACCESS_TO_SPACE, USER_GROUPS_WITH_ACCESS_TO_SPACE_COUNT } from "./gql";
import { SpaceDetailsIdpGroup } from "./types";

type UseSpaceDetailsIdpGroupsProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsIdpGroups = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsIdpGroupsProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    USER_GROUPS_WITH_ACCESS_TO_SPACE,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    USER_GROUPS_WITH_ACCESS_TO_SPACE_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadIdpGroups = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.userGroupsWithAccessToSpace;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const idpGroups = useMemo<SpaceDetailsIdpGroup[]>(() => {
    const edges =
      data?.userGroupsWithAccessToSpace?.edges ||
      previousData?.userGroupsWithAccessToSpace?.edges ||
      [];

    return edges.map(({ node }) => ({
      id: node.userGroup.id,
      name: node.userGroup.groupName,
      roleBindings: node.roles.map((grant) => {
        const grantedVia: SpaceDetailsAccessGrantedVia = {
          kind: "space",
          id: grant.space.id,
          name: grant.space.name,
          source: grant.propagated
            ? SpaceDetailsAccessGrantSource.PROPAGATED
            : SpaceDetailsAccessGrantSource.RESIDENT,
        };

        return {
          id: `${grant.role.id}:${grantedVia.kind}:${grantedVia.id}`,
          role: { id: grant.role.id, name: grant.role.name, isSystem: grant.role.isSystem },
          grantedVia,
        };
      }),
    }));
  }, [data?.userGroupsWithAccessToSpace?.edges, previousData?.userGroupsWithAccessToSpace?.edges]);

  const totalCount =
    countData?.userGroupsWithAccessToSpaceSuggestions.filteredCount ??
    previousCountData?.userGroupsWithAccessToSpaceSuggestions.filteredCount ??
    0;

  return {
    loadIdpGroups,
    error,
    idpGroups,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.userGroupsWithAccessToSpace?.pageInfo.endCursor,
    startCursor: data?.userGroupsWithAccessToSpace?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsIdpGroups;
